import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useContextOptions, useContextSyncCheck } from "../useContextWithSync";
import type { ForecastWorkspace } from "../useForecastWorkspace/useForecastWorkspace";

/** Options for the context options sub-hook (pre-workspace). */
interface UseMainPageContextOptions {
  /** Flat list of all selected cube identifiers. */
  cubeIds: string[];
  /** Current context filter selections from the UI. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Currently selected store status values. */
  selectedStoreStatuses: string[];
}

/**
 * Fetches the context options needed to drive the main page's selectors.
 *
 * Split from the sync-check step so callers can run the workspace
 * (which needs ``resolvedStoreLocations``) between fetching options and
 * evaluating whether the UI has drifted from the last applied snapshot.
 */
export function useMainPageContext(options: UseMainPageContextOptions) {
  return useContextOptions(options);
}

/** Options for the main page's context-sync check (post-workspace). */
interface UseMainPageContextSyncOptions {
  /** Flat list of cube identifiers currently selected in the UI. */
  cubeIds: string[];
  /** Filters the workspace is currently rendering — source of truth for sync. */
  appliedFilters: ForecastWorkspace["appliedFilters"];
  /** Context returned by {@link useMainPageContext} for this render. */
  context: ReturnType<typeof useContextOptions>;
}

/**
 * Computes ``isContextDirty`` / ``changedFields`` against the workspace's
 * live ``appliedFilters``. Decoupled from option fetching so the latest
 * applied snapshot is threaded in directly, avoiding the one-render-lag
 * that a useState mirror + useEffect would introduce.
 */
export function useMainPageContextSync({
  cubeIds,
  appliedFilters,
  context
}: UseMainPageContextSyncOptions) {
  return useContextSyncCheck({
    baseContextOptions: context.baseContextOptions as
      | Record<string, string[]>
      | undefined,
    currentContextForSync: context.currentContextForSync,
    appliedContext: appliedFilters.context as Record<
      string,
      string[] | undefined
    >,
    extraSyncFields: [
      {
        name: "cube versions",
        current: JSON.stringify(cubeIds),
        applied: JSON.stringify(appliedFilters.cubeIds)
      }
    ]
  });
}
